// src/pages/Retention.jsx
// Control Panel → Data Retention (3.13.1, Super Admins): fax records are
// kept 24 months, then deleted by the backend's daily job (5 AM IST). Shows
// the policy and an on/off switch, what the next run would delete, when it
// last checked, and every run that deleted records (when, cutoff, how
// many, by account). Billing isn't affected: closed months are frozen.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import EmptyState from '../components/EmptyState';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' ' + tzLabel(new Date(iso));
}
const fmtDay = iso => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—');
const n = v => Number(v || 0).toLocaleString('en-US');

export default function Retention() {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/retention`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) { setError(e.message); }
  }, [getToken]);
  // Loaded once (Refresh reloads)
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggle() {
    const enabled = !data.settings.enabled;
    const ok = await confirm({
      title: enabled ? 'Switch the clean-up on?' : 'Switch the clean-up off?',
      message: enabled
        ? `Every day at 5 AM IST, fax records older than ${data.settings.months} months will be deleted for every client.`
        : 'Fax records will be kept indefinitely until you switch it on again.',
      confirmLabel: enabled ? 'Switch on' : 'Switch off', danger: !enabled,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/retention`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      notify({ tone: 'success', title: enabled ? 'Data clean-up switched on' : 'Data clean-up switched off' });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't save that", detail: e.message }); }
    setSaving(false);
  }

  if (error) return <div style={S.errorBanner}>{error}</div>;
  if (!data) return <Loader />;
  const { settings, preview, runs, names, targets } = data;

  return (
    <div>
      <h3 style={S.title}><Emo e="🗄️" /> Data Retention</h3>
      <p style={S.sub}>
        Fax records (the rows in Outbox and Inbox, and failed received faxes) are kept for <strong>{settings.months} months</strong>, then deleted
        automatically by the portal every day at 5 AM IST, for every client. Fax files are already deleted after 90 days by the storage rule.
        Billing isn't affected: closed months are recorded and never change. To keep older records, download them (Excel) from the fax lists before they expire.
      </p>

      <div style={S.card}>
        <div style={S.row}>
          <div>
            <div style={S.cardTitle}>Daily clean-up: {settings.enabled ? <span style={S.on}>On</span> : <span style={S.off}>Off</span>}</div>
            <div style={S.muted}>
              Last checked {fmtDateTime(settings.lastCheckedAt)}
              {settings.updatedBy && <> · switched {settings.enabled ? 'on' : 'off'} by {settings.updatedBy} {fmtDateTime(settings.updatedAt)}</>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={load} style={S.ghostBtn}><Emo e="↻" /> Refresh</button>
            <button onClick={toggle} disabled={saving} style={settings.enabled ? S.dangerBtn : S.primaryBtn}>
              {saving ? 'Saving…' : settings.enabled ? 'Switch off' : 'Switch on'}
            </button>
          </div>
        </div>
        <div style={S.next}>
          <strong>Next run</strong> deletes records from before <strong>{fmtDay(preview.cutoff)}</strong>:{' '}
          {preview.total
            ? targets.map((t, i) => <span key={t.key}>{i ? ' · ' : ''}{t.label}: <strong>{n(preview.counts[t.key])}</strong></span>)
            : <>nothing yet (no records are that old).</>}
          {!settings.enabled && preview.total > 0 && <span style={S.off}> (not while it's off)</span>}
        </div>
      </div>

      <h4 style={S.h4}>Clean-up history</h4>
      <p style={S.muted}>Every run that deleted records. Days with nothing to delete aren't listed.</p>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <th style={S.th}>When</th>
              <th style={S.th}>Older than</th>
              {targets.map(t => <th key={t.key} style={S.th}>{t.label}</th>)}
              <th style={S.th}>Total</th>
              <th style={S.th}>By client</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr><td colSpan={4 + targets.length} style={S.emptyCell}>
                <EmptyState icon={<Emo e="🗄️" />} emptyText={preview.firstDeletion ? `Nothing has been deleted yet. The oldest records (${fmtDay(preview.oldest)}) reach ${settings.months} months on ${fmtDay(preview.firstDeletion)}.` : 'Nothing has been deleted yet.'} />
              </td></tr>
            ) : runs.map((r, i) => (
              <tr key={r.id} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(r.at)}{r.trigger !== 'daily' && <div style={S.small}>{r.trigger}</div>}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtDay(r.cutoff)}</td>
                {targets.map(t => <td key={t.key} style={S.tdNum}>{n(r.deleted?.[t.key])}</td>)}
                <td style={{ ...S.tdNum, fontWeight: 700 }}>{n(r.total)}{r.capped && <div style={S.small}>more next day</div>}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{Object.entries(r.byAccount || {}).map(([id, c]) => `${names[id] || id}: ${n(c)}`).join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  title    : { margin: '0 0 6px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub      : { margin: '0 0 16px', color: 'var(--t-546e7a)', fontSize: 13, lineHeight: 1.6, maxWidth: 980 },
  h4       : { margin: '22px 0 4px', color: 'var(--t-00695c)', fontSize: 15 },
  muted    : { color: 'var(--t-78909c)', fontSize: 13, margin: '0 0 10px' },
  small    : { fontSize: 11, color: 'var(--t-78909c)' },
  card     : { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)' },
  row      : { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--t-37474f)', marginBottom: 4 },
  on       : { color: 'var(--t-2e7d32)', fontWeight: 700 },
  off      : { color: 'var(--t-b71c1c)', fontWeight: 700 },
  next     : { marginTop: 12, padding: '10px 14px', background: 'var(--c-f0faf8)', borderRadius: 8, fontSize: 13, color: 'var(--t-37474f)', lineHeight: 1.6 },
  primaryBtn: { padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  dangerBtn : { padding: '8px 16px', background: 'var(--c-ffffff)', color: 'var(--t-b71c1c)', border: '1.5px solid var(--c-ef9a9a)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  ghostBtn  : { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  errorBanner: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table    : { width: '100%', borderCollapse: 'collapse' },
  thead    : { background: 'var(--c-00695c)' },
  th       : { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr       : { borderBottom: '1px solid var(--c-f0f0f0)' },
  td       : { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  tdNum    : { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', textAlign: 'right' },
  emptyCell: { textAlign: 'center', padding: '40px 0' },
};
